import React, { useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DaysOffCalendarProps {
  value: string[];
  onChange: (daysOff: string[]) => void;
}

const WEEKDAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];

function isoDateOf(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function dateFromIso(isoDate: string): Date {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function monthCells(visibleMonth: Date): Date[] {
  const firstOfMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const lastOfMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
  const cellCount = Math.ceil((firstOfMonth.getDay() + lastOfMonth.getDate()) / 7) * 7;
  return Array.from(
    { length: cellCount },
    (_, cellIndex) =>
      new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth(), 1 - firstOfMonth.getDay() + cellIndex),
  );
}

export default function DaysOffCalendar({ value, onChange }: DaysOffCalendarProps) {
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const todayIso = isoDateOf(new Date());
  const sortedDaysOff = [...value].sort();

  const toggleDay = (isoDate: string) => {
    if (value.includes(isoDate)) {
      onChange(value.filter((dayOff) => dayOff !== isoDate));
    } else {
      onChange([...value, isoDate]);
    }
  };

  const shiftMonth = (offset: number) => {
    setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + offset, 1));
  };

  return (
    <div className="flex flex-wrap items-start gap-4">
      <div className="w-full max-w-[250px] space-y-1.5 rounded-md border p-2.5">
        <div className="flex items-center justify-between text-sm font-bold">
          <span>{visibleMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title="Previous month"
              onClick={() => shiftMonth(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title="Next month"
              onClick={() => shiftMonth(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center text-xs tabular-nums">
          {WEEKDAY_LETTERS.map((letter, letterIndex) => (
            <span key={`${letter}-${letterIndex}`} className="pb-0.5 text-[10.5px] font-bold text-muted-foreground">
              {letter}
            </span>
          ))}
          {monthCells(visibleMonth).map((cellDate) => {
            const isoDate = isoDateOf(cellDate);
            const isDayOff = value.includes(isoDate);
            const isOtherMonth = cellDate.getMonth() !== visibleMonth.getMonth();
            return (
              <button
                key={isoDate}
                type="button"
                onClick={() => toggleDay(isoDate)}
                className={cn(
                  "rounded-md py-1",
                  isOtherMonth && "text-muted-foreground opacity-50",
                  isoDate === todayIso && "font-bold ring-[1.5px] ring-inset ring-primary",
                  isDayOff ? "bg-destructive-soft font-bold text-destructive-soft-foreground" : "hover:bg-muted",
                )}
              >
                {cellDate.getDate()}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex min-w-[160px] flex-1 flex-wrap content-start gap-1.5">
        <p className="w-full text-xs text-muted-foreground">
          Pick dates to mark them off. A day off overrides your hours for the whole day.
        </p>
        {sortedDaysOff.map((dayOff) => (
          <span key={dayOff} className="inline-flex items-center gap-1.5 rounded-full border py-0.5 pl-2.5 pr-1 text-xs">
            {dateFromIso(dayOff).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            <button
              type="button"
              title="Remove this day off"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => toggleDay(dayOff)}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
